import type { DateTime } from 'luxon'

import type { FormattedRow } from '@/features/bessDashboard/types'
import { getForecastPriceForHour } from '@/features/bidding/hooks/useHourlyForecastPrices'
import type { ForecastMarketPair, PriceQuantile } from '@/features/bidding/types/forecastPricing'
import { getMarketTimezone } from '@/features/bidding/utils/forecast/convertForecastTimezone'
import { extractHourlyQuantilePrices } from '@/features/bidding/utils/forecast/extractHourlyQuantilePrices'
import { getMarketProgramNameForDirection } from '@/features/bidding/utils/forecast/marketProgramNames'

const HOURS_PER_DAY = 24

export type HourlyPrices = {
  /** Map of hour (0-23) to price */
  prices: Map<number, number>
  /** Whether prices are available for all hours */
  isComplete: boolean
  /** Expected hours for the day (23/24/25 depending on DST) */
  dayHours: number
}

/**
 * Extract hourly prices from forecast data for a market pair, direction, and quantile.
 * Automatically selects the appropriate UP or DOWN forecast based on direction.
 *
 * @param forecastsData - Raw forecast data from API
 * @param marketPair - The market pair (mfrr, fcr-d, fcr-n, opportunity-cost)
 * @param direction - The bid direction ('up' or 'down')
 * @param quantile - The quantile to extract (p10, p25, mean, p75, p90)
 * @param selectedDate - The delivery date to extract prices for
 * @returns Object with prices map and completeness flag
 */
export function deriveForecastPricesForDirection(
  forecastsData: FormattedRow[] | undefined,
  marketPair: ForecastMarketPair,
  direction: 'up' | 'down',
  quantile: PriceQuantile,
  selectedDate: DateTime,
): HourlyPrices {
  const prices = new Map<number, number>()

  if (!forecastsData || forecastsData.length === 0) {
    return { prices, isComplete: false, dayHours: HOURS_PER_DAY }
  }

  const marketProgramName = getMarketProgramNameForDirection(marketPair, direction)
  const marketTimezone = getMarketTimezone()

  // Use the existing infrastructure to extract hourly prices
  const dailyPrices = extractHourlyQuantilePrices(forecastsData, marketProgramName, selectedDate, marketTimezone)

  if (!dailyPrices) {
    return { prices, isComplete: false, dayHours: HOURS_PER_DAY }
  }

  const { dayHours } = dailyPrices

  // Extract the specified quantile for each hour
  for (let hour = 0; hour < dayHours; hour++) {
    const price = getForecastPriceForHour(dailyPrices, hour, quantile)
    if (price !== null) {
      prices.set(hour, price)
    }
  }

  const isComplete = prices.size === dayHours

  return { prices, isComplete, dayHours }
}

/**
 * Get the price for a specific hour from hourly prices.
 * If price is not available for the hour, returns null.
 */
export function getPriceForHour(hourlyPrices: HourlyPrices, hour: number): number | null {
  return hourlyPrices.prices.get(hour) ?? null
}

/**
 * Calculate a fallback daily average price when hourly data is incomplete.
 */
export function getFallbackDailyPrice(hourlyPrices: HourlyPrices): number | null {
  if (hourlyPrices.prices.size === 0) {
    return null
  }

  let sum = 0
  for (const price of hourlyPrices.prices.values()) {
    sum += price
  }

  return sum / hourlyPrices.prices.size
}
